import { LangOptions, LanguageProvider } from "../language-provider.ts";
import { httpRequestWithRetry as fetch } from "../../http-request.ts";
import { models, Model } from "aimodels";
import { LangContentPart, LangImageInput } from "../language-provider.ts";
import { calculateModelResponseTokens } from "../utils/token-calculator.ts";
import { LangMessage, LangMessages, LangMessageItemImage, LangMessageItemTool, LangTool, fixToolResultsIfNeeded } from "../messages.ts";
import { addInstructionAboutSchema } from "../prompt-for-json.ts";

export type GoogleLangOptions = {
  apiKey: string;
  model?: string;
  systemPrompt?: string;
  maxTokens?: number;
  defaultOptions?: LangOptions;
};

export class GoogleLang extends LanguageProvider {
  private _apiKey: string;
  private _model: string;
  private _systemPrompt: string;
  private _maxTokens?: number;
  private modelInfo?: Model;

  constructor(options: GoogleLangOptions) {
    const modelName = options.model || "gemini-2.5-pro";
    super(modelName, options.defaultOptions);

    const modelInfo = models.id(modelName);
    if (!modelInfo) {
      console.error(`Invalid Google model: ${modelName}. Model not found in aimodels database.`);
    }

    this.modelInfo = modelInfo;
    this._apiKey = options.apiKey;
    this._model = modelName;
    this._systemPrompt = options.systemPrompt || "";
    this._maxTokens = options.maxTokens;
  }

  async ask(
    prompt: string,
    options?: LangOptions,
  ): Promise<LangMessages> {
    const messages = new LangMessages();
    if (this._systemPrompt) {
      messages.instructions = this._systemPrompt;
    }
    messages.addUserMessage(prompt);
    return await this.chat(messages, options);
  }

  async chat(
    messages: LangMessage[] | LangMessages,
    options?: LangOptions,
  ): Promise<LangMessages> {
    const resolvedOptions = this.resolveOptions(options);
    const messageCollection = messages instanceof LangMessages
      ? messages
      : new LangMessages(messages);

    const instructions = this.buildInstructions(messageCollection, resolvedOptions);

    fixToolResultsIfNeeded(messageCollection);

    const contents = this.transformMessagesForProvider(messageCollection as any);

    const maxOutputTokens = this.computeMaxTokens(messageCollection);
    const tools = this.buildTools(messageCollection.availableTools);

    const generationConfig: Record<string, any> = {};
    if (typeof maxOutputTokens === "number") {
      generationConfig.max_output_tokens = maxOutputTokens;
    }

    const requestBody: any = {
      contents,
      ...(instructions ? {
        system_instruction: {
          role: "system",
          parts: [{ text: instructions }],
        },
      } : {}),
      ...(Object.keys(generationConfig).length > 0 ? { generation_config: generationConfig } : {}),
      ...(tools ? { tools } : {}),
      ...(resolvedOptions?.providerSpecificBody ?? {}),
    };

    // Gemini REST v1beta doesn't yet accept responseSchema/responseMimeType; fall back to prompt
    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/${this._model}:generateContent?key=${this._apiKey}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // Some setups still expect the header, so keep both
            "x-goog-api-key": this._apiKey,
            ...(resolvedOptions?.providerSpecificHeaders ?? {}),
          },
          body: JSON.stringify(requestBody),
          signal: resolvedOptions?.signal,
        },
      );

      if (!response.ok) {
        const body = await response.text().catch(() => "");
        throw new Error(
          `Google API request failed with status ${response.status}${body ? `: ${body}` : ""}`,
        );
      }

      const data = await response.json();
      this.applyCandidates(data?.candidates, messageCollection, resolvedOptions?.onResult);
    } catch (error) {
      if ((error as any)?.name === "AbortError") {
        messageCollection.aborted = true;
        (error as any).partialResult = messageCollection;
      }
      throw error;
    }

    messageCollection.finished = true;

    const toolsResults = await messageCollection.executeRequestedTools();
    if (resolvedOptions?.onResult && toolsResults) resolvedOptions.onResult(toolsResults);

    return messageCollection;
  }

  protected transformMessagesForProvider(messages: LangMessages): any[] {
    const mapped: any[] = [];

    for (const msg of messages) {
      if (msg.role === "tool-results") {
        const parts = msg.toolResults.map((tr) => {
          // Google API requires response to be an object, not an array
          // If result is an array, wrap it in an object
          let response: any;
          if (Array.isArray(tr.result)) {
            response = { result: tr.result };
          } else if (typeof tr.result === "object" && tr.result !== null) {
            response = tr.result;
          } else {
            response = { result: tr.result };
          }
          return {
            functionResponse: {
              name: tr.name,
              response,
            },
          };
        });
        mapped.push({ role: "user", parts });
        continue;
      }

      if (msg.role !== "user" && msg.role !== "assistant") {
        continue;
      }

      const parts: any[] = [];

      const legacyContent = (msg as any).content as any;
      if (Array.isArray(legacyContent)) {
        parts.push(...this.mapPartsToGemini(legacyContent as LangContentPart[]));
      } else {
        for (const item of msg.items) {
          if (item.type === "text") {
            parts.push({ text: item.text });
          } else if (item.type === "image") {
            const imagePart = this.mapImageItemToGemini(item as LangMessageItemImage);
            if (imagePart) parts.push(imagePart);
          } else if (item.type === "tool") {
            const toolItem = item as LangMessageItemTool & { thoughtSignature?: string };
            const part: any = {
              function_call: {
                name: toolItem.name,
                args: toolItem.arguments ?? {},
              },
            };
            // Include thoughtSignature at part level (required by Google Gemini API)
            if (toolItem.thoughtSignature) {
              part.thoughtSignature = toolItem.thoughtSignature;
            }
            parts.push(part);
          }
        }
      }

      if (parts.length === 0) {
        parts.push({ text: "" });
      }

      mapped.push({
        role: msg.role === "assistant" ? "model" : "user",
        parts,
      });
    }

    return mapped;
  }

  private mapPartsToGemini(parts: LangContentPart[]): any[] {
    const out: any[] = [];
    for (const p of parts) {
      if (p.type === 'text') {
        out.push({ text: p.text });
      } else if (p.type === 'image') {
        const inlineData = this.imageInputToGeminiInlineData(p.image);
        out.push({ inlineData });
      }
    }
    return out;
  }

  private imageInputToGeminiInlineData(image: LangImageInput): { mimeType: string; data: string } {
    const kind: any = (image as any).kind;
    if (kind === 'base64') {
      const base64 = (image as any).base64 as string;
      const mimeType = (image as any).mimeType || 'image/png';
      return { mimeType, data: base64 };
    }
    if (kind === 'url') {
      const url = (image as any).url as string;
      if (url.startsWith('data:')) {
        const match = url.match(/^data:([^;]+);base64,(.*)$/);
        if (!match) throw new Error('Invalid data URL for Gemini image');
        const mimeType = match[1];
        const data = match[2];
        return { mimeType, data };
      }
      throw new Error("Gemini inline image requires base64 or data URL. Provide base64+mimeType or a data: URL.");
    }
    if (kind === 'bytes' || kind === 'blob') {
      throw new Error("Gemini image input requires base64. Convert bytes/blob to base64 first.");
    }
    throw new Error('Unknown image input kind for Gemini');
  }

  private mapImageItemToGemini(image: LangMessageItemImage): any | null {
    if (typeof image.base64 === "string" && image.base64.length > 0) {
      return {
        inlineData: {
          mimeType: image.mimeType || "image/png",
          data: image.base64,
        },
      };
    }

    if (typeof image.url === "string" && image.url.length > 0) {
      if (image.url.startsWith("data:")) {
        const match = image.url.match(/^data:([^;]+);base64,(.*)$/);
        if (!match) return null;
        const mimeType = match[1];
        const data = match[2];
        return {
          inlineData: { mimeType, data },
        };
      }

      return { fileData: { fileUri: image.url } };
    }

    return null;
  }

  private buildInstructions(messageCollection: LangMessages, options?: LangOptions): string {
    let instructions = messageCollection.instructions || "";
    if (this._systemPrompt) {
      instructions = instructions
        ? `${this._systemPrompt}\n\n${instructions}`
        : this._systemPrompt;
    }

    if (options?.schema) {
      const baseInstruction = instructions !== "" ? `${instructions}\n\n` : "";
      instructions = baseInstruction + addInstructionAboutSchema(options.schema);
      messageCollection.instructions = instructions;
    }

    return instructions;
  }

  private computeMaxTokens(messageCollection: LangMessages): number | undefined {
    if (this._maxTokens !== undefined) return this._maxTokens;
    if (!this.modelInfo) return undefined;
    return calculateModelResponseTokens(
      this.modelInfo,
      messageCollection,
      this._maxTokens,
    );
  }

  private buildTools(availableTools?: LangTool[]) {
    if (!availableTools || !Array.isArray(availableTools) || availableTools.length === 0) {
      return undefined;
    }

    const functionDeclarations = availableTools.map((tool) => ({
      name: tool.name,
      description: (tool as any).description || "",
      parameters: (tool as any).parameters,
    }));

    return functionDeclarations.length > 0 ? [{ function_declarations: functionDeclarations }] : undefined;
  }

  private applyCandidates(
    candidates: any[] | undefined,
    result: LangMessages,
    onResult?: (msg: LangMessage) => void,
  ): void {
    if (!Array.isArray(candidates) || candidates.length === 0) {
      return;
    }

    const candidate = candidates[0];
    const parts = candidate?.content?.parts;
    if (!Array.isArray(parts) || parts.length === 0) return;

    const assistantMessage = new LangMessage("assistant", []);
    let toolIndex = 0;

    for (const part of parts) {
      if (!part) continue;
      if (typeof part.text === "string" && part.text.length > 0) {
        assistantMessage.items.push({ type: "text", text: part.text });
      }
      if (part.inlineData && (part.inlineData.data || part.inlineData.b64_json)) {
        const base64 = part.inlineData.data || part.inlineData.b64_json;
        const mimeType = part.inlineData.mimeType || "image/png";
        assistantMessage.items.push({ type: "image", base64, mimeType });
      }
      if (part.fileData?.fileUri) {
        assistantMessage.items.push({ type: "image", url: part.fileData.fileUri });
      }
      const funcCall = part.functionCall || part.function_call;
      if (funcCall) {
        const name = funcCall.name || `function_call_${toolIndex}`;
        const callId = `function_call_${toolIndex++}`;
        const rawArgs = funcCall.args || funcCall.arguments;
        const args = this.parseFunctionArgs(rawArgs);
        const toolItem: any = {
          type: "tool",
          callId,
          name,
          arguments: args,
        };
        // Store thoughtSignature if present (required by Google Gemini API)
        // Google API returns thoughtSignature (camelCase) at the part level
        const thoughtSignature = part.thoughtSignature || funcCall?.thoughtSignature;
        if (thoughtSignature) {
          toolItem.thoughtSignature = thoughtSignature;
        }
        assistantMessage.items.push(toolItem);
      }
    }

    if (assistantMessage.items.length > 0) {
      result.push(assistantMessage);
      onResult?.(assistantMessage);
    }
  }

  private parseFunctionArgs(rawArgs: any): Record<string, any> {
    if (!rawArgs) return {};
    if (typeof rawArgs === "object") return rawArgs;
    if (typeof rawArgs === "string") {
      try {
        return JSON.parse(rawArgs);
      } catch {
        return {};
      }
    }
    return {};
  }
}
